import { useTranslation } from "react-i18next";
import { isJsonObject } from "../../../api/http/jsonDecoding";
import { CURATION_STATES, type CurationState } from "../../../api/knowledgeData/knowledgeDataDecoders";
import type { UiError } from "../../../errors/uiError";

/** One entry of a `dataset_member_invalid` refusal's `details.problems` (knowledge-data.md). */
type MemberProblem =
  | { readonly extraction: string; readonly state: CurationState | null }
  | { readonly lineage: string; readonly members: readonly string[]; readonly acceptedHead: string | null };

/** The refusal's problems by shape; anything else is left to the error notice. */
function memberProblems(error: UiError): MemberProblem[] {
  if (error.kind !== "problem" || error.problem.code !== "dataset_member_invalid") return [];
  const problems: unknown = error.problem.details["problems"];
  return (Array.isArray(problems) ? problems : []).flatMap((problem: unknown): MemberProblem[] => {
    if (!isJsonObject(problem)) return [];
    const { extraction, reason, lineage, members, accepted_head: acceptedHead } = problem;
    if (typeof extraction === "string" && typeof reason === "string") {
      return [{ extraction, state: CURATION_STATES.find((state) => state === reason) ?? null }];
    }
    const names: unknown[] = Array.isArray(members) ? members : [];
    if (typeof lineage === "string" && names.every((name): name is string => typeof name === "string") && (acceptedHead === null || typeof acceptedHead === "string")) {
      return [{ lineage, members: names, acceptedHead }];
    }
    return [];
  });
}

export default function MemberProblems({ error }: { error: UiError }) {
  const { t } = useTranslation();
  const problems = memberProblems(error);
  if (problems.length === 0) return null;
  return (
    <ul className="app-text-muted list-disc space-y-1 pl-5 text-xs">
      {problems.map((problem) => "extraction" in problem ? (
        <li key={problem.extraction} className="break-all">
          {problem.state === null
            ? t("data.dataset.problems.notFound", { extraction: problem.extraction })
            : t("data.dataset.problems.notAccepted", { extraction: problem.extraction, state: t(`data.states.${problem.state}`) })}
        </li>
      ) : (
        <li key={problem.lineage} className="break-all">
          {problem.acceptedHead === null
            ? t("data.dataset.problems.multipleRevisionsNoHead", { lineage: problem.lineage, members: problem.members.join(", ") })
            : t("data.dataset.problems.multipleRevisions", { lineage: problem.lineage, members: problem.members.join(", "), head: problem.acceptedHead })}
        </li>
      ))}
    </ul>
  );
}
